import React, { useEffect, useMemo, useState } from "react";
import { Resource } from "@/bryntum-lib/models/Resource";
import { Input } from "@/modern/components/ui/input";
import { Textarea } from "@/modern/components/ui/textarea";
import { Button } from "@/modern/components/ui/button";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { resourceSchema, ResourceFormData } from "@/react/schema/resourceSchema";
import { toast } from "sonner";
import { RESOURCE_LABEL_MAX_LENGTH } from "@/other/constants/OtherKeys";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import type { ISwivuStore } from "@/types/SwivuStore";
import { ENTITY_TYPES } from "@/other/constants/OtherKeys";
import { SwivuDuplicateEntityHelper } from "@/react/components/SwivuDuplicateEntityHelper";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { BaseModal } from "@/react/components/editors/base/BaseModal";
import { BaseModalBody } from "@/react/components/editors/base/BaseModalBody";
import { useConfirm } from "@omit/react-confirm-dialog";
import { EditorFormField } from "../components/EditorFormField";
import { EditorSection } from "../components/EditorSection";
import { EditorAuditFooter } from "../components/EditorAuditFooter";
import { formatAuditDate, type AuditItem } from "../utils/editorUtils";
import { EditorMetaDetails } from "../components/EditorMetaDetails";
import { EDITOR_META_ITEMS } from "../constants/editorMetaItems";
import { useWarRoom } from "@/react/hooks/useWarRoom";
import { EditorWarRoomToggle } from "../components/EditorWarRoomToggle";

interface ResourceGeneralEditorProps {
  record: {
    resource: Resource;
    onAfterRecordAdd?: (resourceRecord: Resource) => void;
  };
  onClose: () => void;
  editorInstance?: {
    className?: string;
    [key: string]: unknown;
  };
}

const resourceMetaItems = EDITOR_META_ITEMS[ENTITY_TYPES.RESOURCE];

export const ResourceGeneralEditor = ({ record, onClose, editorInstance }: ResourceGeneralEditorProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const { project, entityManager } = useSwivuBryntumContext();
  const { isWarRoom, toggleWarRoom } = useWarRoom("resource-editor");
  const { resource: resourceRecord, onAfterRecordAdd } = record;
  const { subscribe } = useSwivuStore<Resource>({ storeId: STORE_KEYS.resourceStore });
  const confirm = useConfirm();

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    trigger,
    formState: { errors, isDirty, isValid },
  } = useForm<ResourceFormData>({
    resolver: zodResolver(resourceSchema),
    defaultValues: {
      name: resourceRecord.name || "",
      description: resourceRecord.description || "",
      location: resourceRecord.location || "",
      statusId: resourceRecord.statusId || null,
      categoryId: resourceRecord.categoryId || null,
      teamId: resourceRecord.teamId || null,
      tagsId: resourceRecord.tagsId || null,
      iconId: resourceRecord.iconId || null,
      typeId: resourceRecord.typeId || null,
      jobTitle: resourceRecord.jobTitle || "",
    },
  });

  const nameValue = watch("name");

  useEffect(() => {
    return subscribe(({ record: changedRecord }) => {
      if (resourceRecord.id === changedRecord.id) {
        reset({
          name: changedRecord.name || "", description: changedRecord.description || "",
          location: changedRecord.location || "", statusId: changedRecord.statusId || null,
          categoryId: changedRecord.categoryId || null, teamId: changedRecord.teamId || null,
          tagsId: changedRecord.tagsId || null, iconId: changedRecord.iconId || null,
          typeId: changedRecord.typeId || null, jobTitle: changedRecord.jobTitle || "",
        });
        return true;
      }
      return false;
    });
  }, [resourceRecord, reset, subscribe]);

  const onSubmit = async (data: ResourceFormData) => {
    try {
      setIsSaving(true);
      resourceRecord.set(data);
      if (resourceRecord.isPhantom) {
        project.resourceStore.add(resourceRecord);
        onAfterRecordAdd?.(resourceRecord);
      }
      await project.sync();
      toast.success("Resource saved successfully");
    } catch (error) {
      console.error("Error saving resource:", error);
      toast.error("Something went wrong while saving the resource. Please try again.", {
        description: "If the problem persists, please contact support.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => handleSubmit(onSubmit)();

  const handleClose = async () => {
    if (isDirty) {
      const confirmed = await confirm({
        title: "Unsaved Changes",
        description: "You have unsaved changes. What would you like to do?",
        alertDialogOverlay: { className: "z-[--swivu-bryntum-z-index]" },
        alertDialogContent: { className: "z-[--swivu-bryntum-z-index]" },
        customActions: ({ confirm, cancel }: { confirm: () => void; cancel: () => void }) => (
          <div className="flex w-full justify-end gap-3">
            <Button variant="ghost" onClick={cancel} size="sm">Keep working</Button>
            <Button variant="destructive" onClick={confirm} size="sm">Do not save</Button>
            <Button variant="success" size="sm" onClick={async () => {
              if (!isValid) { await trigger("name"); cancel(); return; }
              handleSave(); confirm();
            }}>Save & close</Button>
          </div>
        ),
      });
      if (confirmed) onClose();
      return;
    }
    onClose();
  };

  const openDashboard = () => {
    if (entityManager) {
      entityManager.openEntityManager({ entityType: ENTITY_TYPES.RESOURCE, entityId: resourceRecord.id });
    }
  };

  const handleSaveAndDashboard = async () => {
    await handleSave();
    if (isValid) { openDashboard(); onClose(); }
  };

  const hideDashboardButton = entityManager.isOpen && entityManager.entityId == resourceRecord.id;

  const auditItems = useMemo<AuditItem[]>(() => {
    const items: AuditItem[] = [];
    if (resourceRecord.creatorRecord?.name && resourceRecord.createdAt)
      items.push({ key: "created", label: "Created", value: `${resourceRecord.creatorRecord.name} - ${formatAuditDate(resourceRecord.createdAt)}` });
    if (resourceRecord.updaterRecord?.name && resourceRecord.updatedAt)
      items.push({ key: "updated", label: "Updated", value: `${resourceRecord.updaterRecord.name} - ${formatAuditDate(resourceRecord.updatedAt)}` });
    return items;
  }, [resourceRecord.createdAt, resourceRecord.creatorRecord?.name, resourceRecord.updatedAt, resourceRecord.updaterRecord?.name]);

  const headerRenderer = () => (
    <div className="drag-handle flex items-center gap-4 p-5 border-b border-border/50 rounded-t-lg">
      <h2 className="text-xl font-bold tracking-tight text-foreground">General Settings</h2>
      <p className="text-sm text-muted-foreground">Resources can be people, tools, trucks, equipment, facilities, permits, and more.</p>
      <div className="ml-auto">
        <EditorWarRoomToggle pressed={isWarRoom} onPressedChange={toggleWarRoom} />
      </div>
      <button
        onClick={handleClose}
        className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-6 -top-5 -right-5 bg-background p-3 rounded-full border opacity-100 !size-12"
      >
        <i className="fa-light fa-times text-swivu-icon" />
        <span className="sr-only">Close</span>
      </button>
    </div>
  );

  return (
    <BaseModal
      record={resourceRecord}
      onClose={handleClose}
      title=""
      description=""
      editorInstance={editorInstance}
      hideMenu={true}
      size={{ width: "66rem", height: "min(48rem, 90vh)", maxHeight: "90vh", maxWidth: "90vw", minWidth: "66rem", minHeight: "min(48rem, 90vh)" }}
      modal={false}
      version={undefined}
      swivuAssistanceConfig={undefined}
      footerRenderer={() => (
        <div className="w-full">
          <div className="flex w-full flex-col gap-3 py-2 md:flex-row md:items-center md:justify-between">
            <EditorAuditFooter items={auditItems} isSaving={isSaving} />
            <div className="flex flex-wrap items-center justify-end gap-2">
              <Button onClick={handleSave} disabled={!isDirty || isSaving}>Save Changes</Button>
              {!hideDashboardButton && (
                <Button
                  type="button"
                  disabled={(resourceRecord.isPhantom && !isDirty) || isSaving}
                  onClick={() => {
                    if (!isDirty && !resourceRecord.isPhantom) { openDashboard(); onClose(); }
                    else { handleSaveAndDashboard(); }
                  }}
                >
                  Open Dashboard <i className="fa-solid fa-arrow-right -rotate-45" />
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
      headerRenderer={headerRenderer}
      headerIconCls=""
      className={`bg-background resize-none overflow-visible ${editorInstance?.className || ""}`}
      style={{ zoom: isWarRoom ? 1.15 : 1 }}
    >
      <BaseModalBody contentElementClassName="bg-transparent" className="!p-0">
        <form className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto p-6 space-y-6">

            {/* Section 1 — Identity */}
            <EditorSection>
              {/* Resource ID */}
              <EditorFormField label="Resource ID" required error={errors.name?.message} hint={`${nameValue?.length || 0}/${RESOURCE_LABEL_MAX_LENGTH} characters`}>
                <Controller
                  name="name"
                  control={control}
                  disabled={isSaving}
                  render={({ field }) => {
                    const DuplicateHelperComponent = SwivuDuplicateEntityHelper as React.ComponentType<{
                      record: Resource; store: ISwivuStore<Resource>; placeholder: string; editorReactState: unknown;
                      onChange: (params: { value: string; record?: Resource }) => void; disabled: boolean;
                      editorRecordKey: string; inputElementClassName: string; name: string; value: string; onBlur: () => void; maxLength?: number;
                    }>;
                    return (
                      <DuplicateHelperComponent
                        {...field}
                        record={resourceRecord}
                        store={project.resourceStore}
                        placeholder="Enter resource ID"
                        editorReactState={editorInstance}
                        maxLength={RESOURCE_LABEL_MAX_LENGTH}
                        onChange={({ value, record: selectedRecord }: { value: string; record?: Resource }) => {
                          if (selectedRecord) {
                            reset({
                              name: selectedRecord.name || "", description: selectedRecord.description || "",
                              location: selectedRecord.location || "", statusId: selectedRecord.statusId || null,
                              categoryId: selectedRecord.categoryId || null, teamId: selectedRecord.teamId || null,
                              tagsId: selectedRecord.tagsId || null, iconId: selectedRecord.iconId || null,
                              typeId: selectedRecord.typeId || null, jobTitle: selectedRecord.jobTitle || "",
                            });
                            if (resourceRecord.isPhantom) onAfterRecordAdd?.(selectedRecord);
                          } else {
                            if (value.length <= RESOURCE_LABEL_MAX_LENGTH) field.onChange(value);
                          }
                        }}
                        disabled={isSaving}
                        editorRecordKey="resource"
                        inputElementClassName="flex w-full rounded-md text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-base border border-input bg-background h-swivu-input px-3 py-2"
                      />
                    );
                  }}
                />
              </EditorFormField>

              {/* Location */}
              <EditorFormField label="Location" htmlFor="location" error={errors.location?.message}>
                <Controller
                  name="location"
                  control={control}
                  disabled={isSaving}
                  render={({ field }) => (
                    <Input
                      id="location"
                      variant="outline"
                      placeholder="Enter location"
                      autoComplete="off"
                      {...field}
                      className={errors.location ? "border-destructive" : ""}
                    />
                  )}
                />
              </EditorFormField>

              {/* Description */}
              <EditorFormField label="Description" htmlFor="description" error={errors.description?.message}>
                <Controller
                  name="description"
                  control={control}
                  disabled={isSaving}
                  render={({ field }) => (
                    <Textarea
                      id="description"
                      placeholder="Required description for desktop and mobile..."
                      autoComplete="off"
                      {...field}
                      className={`min-h-[100px] !text-base resize-y${errors.description ? " border-destructive" : ""}`}
                    />
                  )}
                />
              </EditorFormField>
            </EditorSection>

            {/* Section 2 — Classification & Tags */}
            <EditorSection withDivider>
              <EditorMetaDetails
                record={resourceRecord}
                storeKey={STORE_KEYS.resourceStore}
                metaItems={resourceMetaItems}
                isDirectUpdates={false}
                onFieldChange={(changes) => setValue(changes.fieldName as keyof ResourceFormData, changes.value, { shouldDirty: true })}
                isFieldDisabled={isSaving}
              />
            </EditorSection>

          </div>
        </form>
      </BaseModalBody>
    </BaseModal>
  );
};
